import { useState, useEffect, createContext, useContext, useCallback } from "react";
import api from "@/lib/api";

const PeriodContext = createContext(null);

export const PeriodProvider = ({ children }) => {
  const [periodStartDate, setPeriodStartDate] = useState(null);
  const [sectionPeriods, setSectionPeriods] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get(`/settings`);
      setPeriodStartDate(response.data.period_start_date || null);
      setSectionPeriods(response.data.section_periods || {});
    } catch (error) {
      console.error("Error fetching period settings:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updatePeriod = (newDate) => {
    setPeriodStartDate(newDate);
  };

  const updateSectionPeriod = (section, newDate) => {
    setSectionPeriods(prev => ({ ...prev, [section]: newDate }));
  };

  const getSectionPeriod = (section) => {
    // Section-specific period takes precedence, then global period
    return sectionPeriods[section] || periodStartDate || null;
  };

  const refetchSettings = () => {
    fetchSettings();
  };

  return (
    <PeriodContext.Provider value={{ 
      periodStartDate, 
      sectionPeriods,
      loading, 
      updatePeriod, 
      updateSectionPeriod,
      getSectionPeriod,
      refetchSettings
    }}>
      {children}
    </PeriodContext.Provider>
  );
};

export const usePeriod = () => {
  const context = useContext(PeriodContext);
  if (!context) {
    // Return default values if not in provider (for pages before auth)
    return { 
      periodStartDate: null, 
      sectionPeriods: {},
      loading: false, 
      updatePeriod: () => {}, 
      updateSectionPeriod: () => {},
      getSectionPeriod: () => null,
      refetchSettings: () => {}
    };
  }
  return context;
};

// Helper function to check if a date is within the period
export const isDateInPeriod = (date, periodStartDate) => {
  if (!periodStartDate) return true;
  return date >= periodStartDate;
};

// Helper function to get the effective start date for queries
export const getEffectiveStartDate = (requestedStart, periodStartDate) => {
  if (!periodStartDate) return requestedStart;
  if (!requestedStart) return periodStartDate;
  return requestedStart > periodStartDate ? requestedStart : periodStartDate;
};

export default usePeriod;
